import {
  LegalLayout,
  Heading,
  Text,
  OrderedList,
  UnorderedList,
  ListItem,
  Link,
  RouterLink,
} from '../components/LegalLayout';

export default function RefundPolicy() {
  return (
    <LegalLayout title="Refund Policy" lastUpdated="June 7, 2026">
      <Text>
        This Refund Policy (&ldquo;Policy&rdquo;) describes the terms under
        which IvoryShoulder, LLC, doing business as WagerWatchdog
        (&ldquo;WagerWatchdog,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
        &ldquo;our&rdquo;), provides refunds for paid subscription services.
      </Text>

      <Heading as="h2">1. 30-Day Satisfaction Guarantee</Heading>
      <Text>
        We stand behind the quality of our Services. If you are not satisfied
        with your WagerWatchdog Pro subscription for any reason, you may request
        a full refund within <strong>30 days of your initial Pro signup
        date</strong>.
      </Text>
      <Text>
        This guarantee applies to:
      </Text>
      <UnorderedList>
        <ListItem>
          First-time Pro subscription purchases only.
        </ListItem>
        <ListItem>
          Both monthly and annual subscription plans.
        </ListItem>
      </UnorderedList>
      <Text>
        For annual plans, the full annual amount will be refunded if the request
        is made within 30 days of the initial purchase.
      </Text>

      <Heading as="h2">2. How to Request a Refund</Heading>
      <Text>
        To request a refund, you must submit a support ticket through our
        &ldquo;Report an Issue&rdquo; board within the WagerWatchdog
        application. When submitting your request, please include:
      </Text>
      <OrderedList>
        <ListItem>
          Your account email address.
        </ListItem>
        <ListItem>
          The date of your subscription purchase.
        </ListItem>
        <ListItem>
          A brief description of why you are requesting a refund.
        </ListItem>
      </OrderedList>
      <Text>
        Refund requests submitted through any channel other than the &ldquo;Report
        an Issue&rdquo; support ticket system may not be processed. You may also
        email{' '}
        <Link href="mailto:support@wagerwatchdog.com">
          support@wagerwatchdog.com
        </Link>{' '}
        if you are unable to access the application.
      </Text>

      <Heading as="h2">3. Refund Processing</Heading>
      <Text>
        Once we receive your refund request, we will:
      </Text>
      <OrderedList>
        <ListItem>
          Verify that your request falls within the 30-day eligibility window.
        </ListItem>
        <ListItem>
          Process the refund to your original payment method within 5&ndash;10
          business days.
        </ListItem>
        <ListItem>
          Send you an email confirmation when the refund has been issued.
        </ListItem>
      </OrderedList>
      <Text>
        Please note that your bank or credit card company may require additional
        time to post the refund to your account.
      </Text>

      <Heading as="h2">4. After the 30-Day Period</Heading>
      <Text>
        Refund requests made after the 30-day satisfaction guarantee period will
        be evaluated on a case-by-case basis at our sole discretion. We are not
        obligated to issue refunds after the 30-day period, but we may consider
        them in exceptional circumstances, such as:
      </Text>
      <UnorderedList>
        <ListItem>
          Extended service outages that materially prevented your use of the
          Services.
        </ListItem>
        <ListItem>
          Billing errors on our part.
        </ListItem>
      </UnorderedList>

      <Heading as="h2">5. Subscription Cancellation vs. Refund</Heading>
      <Text>
        Cancelling your subscription and requesting a refund are separate
        actions:
      </Text>
      <UnorderedList>
        <ListItem>
          <strong>Cancellation</strong> stops future billing. Your access
          continues until the end of your current billing period.
        </ListItem>
        <ListItem>
          <strong>Refund</strong> returns the payment for your current or recent
          billing period. Access to paid features will be revoked upon refund
          processing.
        </ListItem>
      </UnorderedList>
      <Text>
        If you receive a refund, your account will be reverted to the free tier
        immediately.
      </Text>

      <Heading as="h2">6. Non-Refundable Items</Heading>
      <Text>The following are not eligible for refunds:</Text>
      <UnorderedList>
        <ListItem>
          Subscription renewals beyond the initial 30-day guarantee period
          (unless approved at our discretion per Section 4).
        </ListItem>
        <ListItem>
          Partial-month usage within a billing cycle.
        </ListItem>
        <ListItem>
          Accounts terminated due to violations of our{' '}
          <Link as={RouterLink} to="/terms">
            Terms of Service
          </Link>
          .
        </ListItem>
      </UnorderedList>

      <Heading as="h2">7. Changes to This Policy</Heading>
      <Text>
        We may update this Refund Policy from time to time. Changes will be
        posted on this page with a revised &ldquo;Last updated&rdquo; date.
        The refund terms in effect at the time of your purchase will apply to
        that transaction.
      </Text>

      <Heading as="h2">8. Contact Us</Heading>
      <Text>
        If you have questions about this Refund Policy, contact us at:
      </Text>
      <Text>
        IvoryShoulder, LLC DBA WagerWatchdog
        <br />
        Email:{' '}
        <Link href="mailto:support@wagerwatchdog.com">
          support@wagerwatchdog.com
        </Link>
      </Text>
    </LegalLayout>
  );
}
